import { ComponentProps, ReactElement, useId } from "react";
import {
  FieldValues,
  UseControllerProps,
  useController,
} from "react-hook-form";
import { Input, Select } from "@industriousoffice/react-components";
import { useTranslation } from "react-i18next";

type PaymentFormInputProps<T extends FieldValues> = UseControllerProps<T> &
  Omit<ComponentProps<typeof Input>, "name" | "defaultValue">;

export function PaymentFormInput<T extends FieldValues>({
  control,
  name,
  rules,
  defaultValue,
  shouldUnregister,
  label,
  isRequired,
  isDisabled,
  ...props
}: PaymentFormInputProps<T>): ReactElement {
  const { t } = useTranslation("paymentMethods");
  const id = useId();
  const {
    field,
    fieldState: { error },
    formState: { isSubmitting },
  } = useController({ control, name, rules, defaultValue, shouldUnregister });
  return (
    <div className="payment-form-input" data-invalid={error != null}>
      <label htmlFor={id}>
        {label}
        {isRequired && <span aria-hidden="true"> *</span>}
      </label>
      <Input
        {...props}
        {...field}
        id={id}
        value={field.value ?? ""}
        isInvalid={error != null}
        isDisabled={isDisabled || isSubmitting}
        aria-required={isRequired}
        aria-invalid={error != null}
        aria-describedby={error == null ? undefined : `${id}-error`}
      />
      <p
        id={`${id}-error`}
        className="payment-error"
        role={error == null ? undefined : "alert"}
      >
        {error?.message == null ? null : t(error.message)}
      </p>
    </div>
  );
}

export function PaymentFormSelect<T extends FieldValues>({
  control,
  name,
  rules,
  defaultValue,
  shouldUnregister,
  label,
  options,
  isRequired,
  isDisabled,
  placeholder,
}: UseControllerProps<T> & {
  label?: string;
  options: { label: string; value: string }[];
  isRequired?: boolean;
  isDisabled?: boolean;
  placeholder?: string;
}): ReactElement {
  const { t } = useTranslation("paymentMethods");
  const id = useId();
  const {
    field,
    fieldState: { error },
    formState: { isSubmitting },
  } = useController({ control, name, rules, defaultValue, shouldUnregister });
  return (
    <div className="payment-form-input" data-invalid={error != null}>
      <span className="payment-form-label" id={`${id}-label`}>
        {label}
        {isRequired && <span aria-hidden="true"> *</span>}
      </span>
      <div
        ref={(node) =>
          field.ref({
            focus: () =>
              node
                ?.querySelector<HTMLInputElement>('input[role="combobox"]')
                ?.focus(),
          })
        }
      >
        <Select
          className="payment-single-select"
          customClasses={{
            control: "payment-select-control",
            menu: "payment-select-menu",
            singleValue: "payment-select-value",
          }}
          name={field.name}
          value={options.find((option) => option.value === field.value) ?? null}
          options={options}
          onChange={(option) => field.onChange(option?.value ?? "")}
          onBlur={field.onBlur}
          isDisabled={isDisabled || isSubmitting}
          isInvalid={error != null}
          isSearchable={false}
          isClearable={false}
          aria-labelledby={`${id}-label`}
          aria-required={isRequired}
          aria-invalid={error != null}
          aria-describedby={error == null ? undefined : `${id}-error`}
          placeholder={placeholder || t("Choose an option")}
        />
      </div>
      <p
        id={`${id}-error`}
        className="payment-error"
        role={error == null ? undefined : "alert"}
      >
        {error?.message == null ? null : t(error.message)}
      </p>
    </div>
  );
}
